import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchGiftCards,
  transferGiftCard,
  redeemGiftCard,
  fetchNotifications,
  fetchStatus,
  markNotificationRead,
} from '../../store/slices/giftCardsSlice';
import { getPointsBalance, fetchVouchers, redeemVoucher } from '../../store/slices/pointsSlice';
import { getUserProfile } from '../../store/slices/authSlice';
import {
  REFERRAL_BODY,
  REFERRAL_HEADLINE,
  REFERRAL_REWARD_EGP,
  referralShareMessage,
  referralShareUrl,
} from '../../constants/referralProgram';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  giftNotificationAmountLabel,
  giftNotificationIcon,
} from '../../utils/giftNotificationDisplay';
import { toast } from 'react-toastify';
import styled, { keyframes } from 'styled-components';

const landingShimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const GiftCardsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 3rem;
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #a8e6cf 0%,
    #88d8a3 35%,
    #a8e6cf 70%,
    #76c7a1 100%
  );
  background-size: 200% 200%;
  animation: ${landingShimmer} 14s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 2rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const PageTitleIcon = styled.span`
  font-size: 2rem;
  line-height: 1;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.85);
`;

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.65rem 1rem;
  background: ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  border: none;
  border-radius: 0.5rem;
  color: ${props => props.active ? '#fff' : 'var(--text-primary)'};
  font-weight: ${props => props.active ? '600' : '500'};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    color: ${props => props.active ? '#fff' : 'var(--primary-color)'};
    background: ${props => props.active ? 'var(--primary-color)' : 'rgba(16, 185, 129, 0.08)'};
  }
`;

const TabIcon = styled.span`
  font-size: 1rem;
  line-height: 1;
`;

const Card = styled.div`
  background-color: rgba(255, 255, 255, 0.97);
  border-radius: 0.75rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.85);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SectionTitleIcon = styled.span`
  font-size: 1.35rem;
  line-height: 1;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 1rem;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  ${props => props.error && `
    border-color: #dc3545;
  `}
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: var(--primary-color);
  color: white;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
  }
`;

// const SecondaryButton = styled(Button)`
//   background-color: transparent;
//   color: var(--text-primary);
//   border: 2px solid var(--border-color);
//   
//   &:hover:not(:disabled) {
//     background-color: var(--bg-secondary);
//   }
// `;

const GiftCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const GiftCardCard = styled.div`
  background: linear-gradient(135deg, #0f766e 0%, #14b8a6 55%, #5eead4 100%);
  border-radius: 0.75rem;
  padding: 1.5rem;
  color: white;
  box-shadow: 0 8px 24px rgba(15, 118, 110, 0.25);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const GiftCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 0.75rem;
  position: relative;
  z-index: 1;
`;

const GiftCardIcon = styled.span`
  font-size: 2.5rem;
  line-height: 1;
  flex-shrink: 0;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
`;

const GiftCardBody = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 1;
`;

const GiftCardCode = styled.div`
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  margin-bottom: 0.35rem;
  font-family: 'Courier New', monospace;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.15);
`;

const GiftCardAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const GiftCardMeta = styled.div`
  font-size: 0.875rem;
  opacity: 0.95;
  margin-top: 0.75rem;
  position: relative;
  z-index: 1;
`;

const NotificationCard = styled.div`
  padding: 1rem 1.15rem;
  background-color: ${props => props.unread ? 'rgba(16, 185, 129, 0.08)' : 'var(--bg-primary)'};
  border-radius: 0.5rem;
  border: 1px solid ${props => props.unread ? 'rgba(16, 185, 129, 0.25)' : 'var(--border-color)'};
  border-left: 4px solid ${props => props.unread ? 'var(--primary-color)' : 'transparent'};
  cursor: ${props => props.unread ? 'pointer' : 'default'};
  transition: all 0.2s;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  
  &:hover {
    background-color: rgba(16, 185, 129, 0.06);
  }
`;

const NotificationIcon = styled.span`
  font-size: 1.75rem;
  line-height: 1;
  flex-shrink: 0;
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => 
    props.status === 'ACTIVE' ? 'rgba(255,255,255,0.3)' :
    props.status === 'REDEEMED' ? 'rgba(76,175,80,0.3)' :
    props.status === 'EXPIRED' ? 'rgba(244,67,54,0.3)' : 'rgba(158,158,158,0.3)'};
  color: white;
  margin-top: 0.5rem;
`;

const NotificationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const NotificationText = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const NotificationDate = styled.div`
  color: var(--text-light);
  font-size: 0.75rem;
  margin-top: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: var(--text-secondary);
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 0.75rem;
  line-height: 1;
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, rgba(168, 230, 207, 0.35) 0%, rgba(136, 216, 163, 0.25) 100%);
  border-radius: 0.75rem;
  padding: 1.5rem;
  text-align: center;
  border: 1px solid rgba(16, 185, 129, 0.15);
`;

const StatIcon = styled.div`
  font-size: 1.75rem;
  margin-bottom: 0.35rem;
  line-height: 1;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const VoucherRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
`;

const VoucherTile = styled.div`
  position: relative;
  border-radius: 0.75rem;
  padding: 1.5rem;
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  opacity: ${(p) => (p.$dim ? 0.55 : 1)};
  transition: opacity 0.2s;
`;

const VoucherHead = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const ProgressRingWrap = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
`;

const ReferralCard = styled.div`
  background: linear-gradient(145deg, #0f766e 0%, #059669 45%, #34d399 100%);
  border-radius: 1rem;
  padding: 1.75rem;
  margin-bottom: 2rem;
  color: #fff;
  box-shadow: 0 12px 40px rgba(5, 150, 105, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ReferralHeadline = styled.h2`
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 0 0.75rem;
  line-height: 1.25;
  color: #fff;
`;

const ReferralBody = styled.p`
  margin: 0 0 1.25rem;
  font-size: 0.95rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.92);
  max-width: 42rem;
`;

const ReferralRewardBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  padding: 0.35rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const ReferralCodePanel = styled.div`
  background: rgba(255, 255, 255, 0.97);
  color: var(--text-primary);
  border-radius: 0.75rem;
  padding: 1.1rem 1.25rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const ReferralCodeLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  margin-bottom: 0.35rem;
`;

const CodeMono = styled.code`
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  font-family: ui-monospace, monospace;
  color: #047857;
`;

const ReferralActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ReferralActionButton = styled.button`
  padding: 0.55rem 1rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  border: 1px solid ${(props) => (props.$primary ? 'transparent' : 'var(--border-color)')};
  background: ${(props) => (props.$primary ? 'var(--primary-color)' : '#fff')};
  color: ${(props) => (props.$primary ? '#fff' : 'var(--text-primary)')};
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const ReferralLoading = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
`;

const GIFT_TABS = [
  { id: 'my-cards', label: 'My Gifts', icon: '🎁' },
  { id: 'transfer', label: 'Transfer Gift', icon: '📤' },
  { id: 'redeem', label: 'Redeem Gift', icon: '🎫' },
  { id: 'notifications', label: 'Notifications', icon: '🔔' },
  { id: 'status', label: 'Status', icon: '📊' },
];

const GiftCardsPage = () => {
  const dispatch = useDispatch();
  const { giftCards, notifications, status, loading, error } = useSelector((state) => state.giftCards);
  const { user } = useSelector((state) => state.auth);
  const { balance, vouchers, vouchersLoading } = useSelector((state) => state.points);
  
  const [activeTab, setActiveTab] = useState('my-cards');
  const [transferData, setTransferData] = useState({
    code: '',
    username: '',
    phone_number: '',
  });
  const [redeemCode, setRedeemCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use only the stable user ID as a dependency — the full `user` object
  // reference changes every time getUserProfile resolves, which would cause
  // an infinite dispatch loop.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    dispatch(fetchGiftCards());
    dispatch(fetchNotifications());
    dispatch(fetchStatus());
    dispatch(getPointsBalance());
    dispatch(fetchVouchers());
    // Do NOT dispatch getUserProfile here — App.js already handles it on auth
    // change, and dispatching it here re-creates the user object, re-triggering
    // this effect endlessly.
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId && !user?.referral_code) {
      dispatch(getUserProfile());
    }
  }, [dispatch, userId, user?.referral_code]);

  const balanceNum = balance != null ? Number(balance) : 0;
  const COST_SERVICE = 15;
  const COST_DELIVERY = 60;

  const ProgressRing = ({ cost }) => {
    const p = Math.min(1, balanceNum / cost);
    const circumference = 2 * Math.PI * 28;
    const offset = circumference * (1 - p);
    return (
      <ProgressRingWrap>
        <svg width="72" height="72" viewBox="0 0 72 72" aria-hidden>
          <circle cx="36" cy="36" r="28" fill="none" stroke="var(--border-color)" strokeWidth="6" />
          <circle
            cx="36"
            cy="36"
            r="28"
            fill="none"
            stroke="var(--primary-color)"
            strokeWidth="6"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 36 36)"
          />
        </svg>
      </ProgressRingWrap>
    );
  };

  const handleRedeemPointsVoucher = async (type) => {
    try {
      await dispatch(redeemVoucher(type)).unwrap();
      dispatch(getPointsBalance());
      dispatch(fetchVouchers());
    } catch {
      /* toast in slice */
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : 'An error occurred');
    }
  }, [error]);

  const handleTransferGiftCard = async (e) => {
    e.preventDefault();
    if (!transferData.code || !transferData.username.trim() || !transferData.phone_number) {
      toast.error('Please fill in all fields — gift code, recipient username, and phone number are all required');
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(transferGiftCard({
        code: transferData.code,
        username: transferData.username.trim(),
        phoneNumber: transferData.phone_number,
      })).unwrap();
      toast.success('Gift transferred successfully!');
      setTransferData({ code: '', username: '', phone_number: '' });
      dispatch(fetchGiftCards());
      dispatch(fetchVouchers());
      dispatch(fetchNotifications());
      dispatch(fetchStatus());
    } catch (error) {
      // Handle referral message for user not found
      const errorData = error || {};
      if (errorData.user_not_found) {
        toast.error(errorData.message || errorData.error || 'User not found', {
          autoClose: 6000, // Show longer for referral message
        });
      } else {
        const errorMessage = errorData.error || errorData.message || (typeof error === 'string' ? error : 'Failed to transfer gift');
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRedeemGiftCard = async (e) => {
    e.preventDefault();
    if (!redeemCode.trim()) {
      toast.error('Please enter a gift code');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await dispatch(redeemGiftCard(redeemCode.trim())).unwrap();
      toast.success(`Gift redeemed! ${result.points_added} points added to your account.`);
      setRedeemCode('');
      dispatch(fetchGiftCards());
      dispatch(getPointsBalance());
      dispatch(fetchStatus());
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to redeem gift');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await dispatch(markNotificationRead(notificationId)).unwrap();
      dispatch(fetchNotifications());
      dispatch(fetchStatus());
    } catch (error) {
      // Silent fail for marking as read
    }
  };

  if (!user) {
    return (
      <GiftCardsContainer>
        <EmptyState>
          <h2>Please log in to manage your gifts</h2>
        </EmptyState>
      </GiftCardsContainer>
    );
  }

  const myGiftCards = giftCards.filter(
    (gc) =>
      gc.creator_id === user.id &&
      gc.status === 'ACTIVE' &&
      !gc.recipient_id &&
      !gc.recipient_phone
  );
  const activeVouchers = (vouchers || []).filter((v) => v.status === 'ACTIVE');
  // const receivedGiftCards = giftCards.filter(gc => gc.recipient_id === user.id && gc.status === 'ACTIVE');
  const unreadNotifications = notifications.filter(n => !n.is_read);

  return (
    <GiftCardsContainer>
      <PageTitle>
        <PageTitleIcon aria-hidden>🎁</PageTitleIcon>
        Gifts
      </PageTitle>

      <ReferralCard>
        <ReferralRewardBadge>+{REFERRAL_REWARD_EGP} EGP per qualified referral</ReferralRewardBadge>
        <ReferralHeadline>{REFERRAL_HEADLINE}</ReferralHeadline>
        <ReferralBody>{REFERRAL_BODY}</ReferralBody>
        <ReferralCodePanel>
          <div>
            <ReferralCodeLabel>Your referral code</ReferralCodeLabel>
            {user?.referral_code ? (
              <CodeMono>{user.referral_code}</CodeMono>
            ) : (
              <ReferralLoading>Generating your code…</ReferralLoading>
            )}
          </div>
          {user?.referral_code && (
            <ReferralActions>
              <ReferralActionButton
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(user.referral_code);
                  toast.success('Referral code copied!');
                }}
              >
                Copy code
              </ReferralActionButton>
              {typeof navigator.share === 'function' && (
                <ReferralActionButton
                  type="button"
                  $primary
                  onClick={() =>
                    navigator.share({
                      title: 'Join me on Flea Marketplace',
                      text: referralShareMessage(user.referral_code),
                      url: referralShareUrl(user.referral_code),
                    }).catch(() => {})
                  }
                >
                  Share link
                </ReferralActionButton>
              )}
            </ReferralActions>
          )}
        </ReferralCodePanel>
      </ReferralCard>

      <Card>
        <SectionTitle>Redeem points for vouchers</SectionTitle>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
          Each redemption issues a unique promo code (UUID). Use it at checkout for the matching benefit. Balance:{' '}
          <strong>🪙 {balanceNum.toLocaleString()} pts</strong>
        </p>
        <VoucherRow>
          <VoucherTile $dim={balanceNum < COST_SERVICE}>
            <VoucherHead>
              <ProgressRing cost={COST_SERVICE} />
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Free service fee</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{COST_SERVICE} points</div>
              </div>
            </VoucherHead>
            <PrimaryButton
              type="button"
              disabled={balanceNum < COST_SERVICE || vouchersLoading}
              onClick={() => handleRedeemPointsVoucher('SERVICE_FEE')}
            >
              Redeem &amp; get code
            </PrimaryButton>
          </VoucherTile>
          <VoucherTile $dim={balanceNum < COST_DELIVERY}>
            <VoucherHead>
              <ProgressRing cost={COST_DELIVERY} />
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Free delivery</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{COST_DELIVERY} points</div>
              </div>
            </VoucherHead>
            <PrimaryButton
              type="button"
              disabled={balanceNum < COST_DELIVERY || vouchersLoading}
              onClick={() => handleRedeemPointsVoucher('DELIVERY')}
            >
              Redeem &amp; get code
            </PrimaryButton>
          </VoucherTile>
        </VoucherRow>
        {activeVouchers.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Your voucher codes</div>
            <ul style={{ paddingLeft: '1.25rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              {activeVouchers.map((v) => (
                <li key={v.id || v.code} style={{ marginBottom: '0.35rem' }}>
                  <CodeMono style={{ fontSize: '0.85rem' }}>{v.code}</CodeMono>{' '}
                  — {v.voucher_type === 'SERVICE_FEE' ? 'Service fee' : 'Delivery'} ({v.points_cost} pts)
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      <Tabs>
        {GIFT_TABS.map((tab) => (
          <Tab
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            <TabIcon aria-hidden>{tab.icon}</TabIcon>
            {tab.label}
            {tab.id === 'notifications' && unreadNotifications.length > 0 && ` (${unreadNotifications.length})`}
          </Tab>
        ))}
      </Tabs>

      {activeTab === 'my-cards' && (
        <Card>
          <SectionTitle>
            <SectionTitleIcon aria-hidden>🎁</SectionTitleIcon>
            My Gifts
          </SectionTitle>
          {loading && giftCards.length === 0 ? (
            <LoadingSpinner text="Loading gifts..." />
          ) : myGiftCards.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon aria-hidden>🎁</EmptyStateIcon>
              <h3>No active gifts</h3>
              <p>You don&apos;t have any active gifts yet.</p>
            </EmptyState>
          ) : (
            <GiftCardGrid>
              {myGiftCards.map((giftCard) => (
                <GiftCardCard key={giftCard.id}>
                  <GiftCardHeader>
                    <GiftCardIcon aria-hidden>🎁</GiftCardIcon>
                    <GiftCardBody>
                      <GiftCardCode>{giftCard.code}</GiftCardCode>
                      <GiftCardAmount>🪙 {giftCard.points_amount} points</GiftCardAmount>
                    </GiftCardBody>
                  </GiftCardHeader>
                  <GiftCardMeta>
                    Created: {new Date(giftCard.created_at).toLocaleDateString()}
                    {giftCard.recipient_phone && (
                      <div>Recipient: {giftCard.recipient_phone}</div>
                    )}
                    <StatusBadge status={giftCard.status}>{giftCard.status}</StatusBadge>
                  </GiftCardMeta>
                </GiftCardCard>
              ))}
            </GiftCardGrid>
          )}
        </Card>
      )}

      {activeTab === 'transfer' && (
        <Card>
          <SectionTitle>
            <SectionTitleIcon aria-hidden>📤</SectionTitleIcon>
            Transfer Gift
          </SectionTitle>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
            To protect both you and the recipient, transfers require both the recipient's username <strong>and</strong> their phone number — both must belong to the same account.
          </p>
          <Form onSubmit={handleTransferGiftCard}>
            <FormGroup>
              <Label htmlFor="transfer_code">Gift Code *</Label>
              <Input
                id="transfer_code"
                type="text"
                value={transferData.code}
                onChange={(e) => setTransferData({ ...transferData, code: e.target.value.toUpperCase() })}
                placeholder="Enter gift code"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="transfer_username">Recipient Username *</Label>
              <Input
                id="transfer_username"
                type="text"
                value={transferData.username}
                onChange={(e) => setTransferData({ ...transferData, username: e.target.value })}
                placeholder="Enter username (e.g., @johndoe)"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="transfer_phone">Recipient Phone Number *</Label>
              <Input
                id="transfer_phone"
                type="tel"
                value={transferData.phone_number}
                onChange={(e) => setTransferData({ ...transferData, phone_number: e.target.value })}
                placeholder="Enter phone number (e.g., +201234567890)"
                required
              />
            </FormGroup>
            <PrimaryButton type="submit" disabled={isSubmitting || loading}>
              {isSubmitting || loading ? 'Transferring...' : 'Transfer Gift'}
            </PrimaryButton>
          </Form>
        </Card>
      )}

      {activeTab === 'redeem' && (
        <Card>
          <SectionTitle>
            <SectionTitleIcon aria-hidden>🎫</SectionTitleIcon>
            Redeem Gift
          </SectionTitle>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
            Enter a gift code to redeem points to your account
          </p>
          <Form onSubmit={handleRedeemGiftCard}>
            <FormGroup>
              <Label htmlFor="redeem_code">Gift Code *</Label>
              <Input
                id="redeem_code"
                type="text"
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                placeholder="Enter gift code"
                required
              />
            </FormGroup>
            <PrimaryButton type="submit" disabled={isSubmitting || loading}>
              {isSubmitting || loading ? 'Redeeming...' : 'Redeem Gift'}
            </PrimaryButton>
          </Form>
        </Card>
      )}

      {activeTab === 'notifications' && (
        <Card>
          <SectionTitle>
            <SectionTitleIcon aria-hidden>🔔</SectionTitleIcon>
            Gift Notifications
          </SectionTitle>
          {loading ? (
            <LoadingSpinner text="Loading notifications..." />
          ) : notifications.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon aria-hidden>🔔</EmptyStateIcon>
              <h3>No notifications</h3>
              <p>You&apos;ll receive notifications when you receive gifts</p>
            </EmptyState>
          ) : (
            <NotificationsList>
              {notifications.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  unread={!notification.is_read}
                  onClick={() => !notification.is_read && handleMarkNotificationRead(notification.id)}
                >
                  <NotificationIcon aria-hidden>{giftNotificationIcon(notification)}</NotificationIcon>
                  <NotificationContent>
                    <NotificationTitle>{notification.message}</NotificationTitle>
                    <NotificationText>
                      From: {notification.sender_name}
                      {notification.sender_phone ? ` (${notification.sender_phone})` : ''}
                    </NotificationText>
                    <NotificationText>
                      {giftNotificationAmountLabel(notification)}
                    </NotificationText>
                    <NotificationDate>
                      {new Date(notification.created_at).toLocaleString()}
                    </NotificationDate>
                  </NotificationContent>
                </NotificationCard>
              ))}
            </NotificationsList>
          )}
        </Card>
      )}

      {activeTab === 'status' && (
        <Card>
          <SectionTitle>
            <SectionTitleIcon aria-hidden>📊</SectionTitleIcon>
            Gift Status
          </SectionTitle>
          {loading ? (
            <LoadingSpinner text="Loading status..." />
          ) : status ? (
            <StatsGrid>
              <StatCard>
                <StatIcon aria-hidden>✨</StatIcon>
                <StatValue>{status.created_count || 0}</StatValue>
                <StatLabel>Created</StatLabel>
              </StatCard>
              <StatCard>
                <StatIcon aria-hidden>🎁</StatIcon>
                <StatValue>{status.received_count || 0}</StatValue>
                <StatLabel>Received</StatLabel>
              </StatCard>
              <StatCard>
                <StatIcon aria-hidden>🔔</StatIcon>
                <StatValue>{status.unread_count || 0}</StatValue>
                <StatLabel>Unread Notifications</StatLabel>
              </StatCard>
            </StatsGrid>
          ) : (
            <EmptyState>
              <p>No status information available</p>
            </EmptyState>
          )}
        </Card>
      )}
    </GiftCardsContainer>
  );
};

export default GiftCardsPage;
